import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImg, API } from "@/lib/api";
import { CATEGORY_LABELS, formatDateTime, readingTime } from "@/lib/format";
import { SiteLayout } from "@/components/site/SiteLayout";
import { ArticleCard } from "@/components/site/ArticleCard";
import { Kicker } from "@/components/site/BreakingTag";
import { Clock, ArrowLeft, MessageCircle, Twitter, Facebook, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null); setNotFound(false);
    api.get(`/articles/${slug}`)
      .then((r) => { setArticle(r.data.article); setRelated(r.data.related); })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <SiteLayout>
      <div className="max-w-[720px] mx-auto px-4 py-24 text-center">
        <h1 className="font-headline font-bold text-3xl text-prussian">Artikel nicht gefunden</h1>
        <Link to="/" className="inline-flex items-center gap-2 text-gold-hover mt-4 font-body"><ArrowLeft size={16} /> Zur Startseite</Link>
      </div>
    </SiteLayout>
  );

  if (!article) return <SiteLayout><div className="py-32 text-center text-muted-foreground font-body">Lädt…</div></SiteLayout>;

  const shareUrl = `${API}/share/${article.slug}`;
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Teil-Link kopiert (mit Bild-Vorschau)"); };
  const shareBtn = "w-9 h-9 flex items-center justify-center border border-edborder text-prussian hover:bg-prussian hover:text-white transition-colors";

  return (
    <SiteLayout>
      <article data-testid="article-detail-container" className="max-w-[760px] mx-auto px-4 py-8">
        <Link to={`/rubrik/${article.category}`} className="inline-flex items-center gap-1.5 mb-4">
          <Kicker breaking={article.is_breaking}>{article.kicker || CATEGORY_LABELS[article.category]}</Kicker>
        </Link>

        <h1 data-testid="article-headline" className="font-headline font-black text-prussian text-3xl md:text-[2.75rem] leading-[1.08] tracking-tight">
          {article.title}
        </h1>

        <p data-testid="article-lead-chapo" className="mt-5 font-headline text-xl md:text-2xl text-charcoal/80 leading-relaxed italic">
          {article.chapo}
        </p>

        <div data-testid="article-author-info" className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-6 pb-6 border-b border-edborder text-sm font-body text-muted-foreground">
          <span className="font-semibold text-prussian">Von {article.author}</span>
          <span>·</span>
          <span>{formatDateTime(article.published_at)}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1"><Clock size={13} /> {readingTime(article.body)} Min. Lesezeit</span>
        </div>

        <div data-testid="article-share-bar" className="flex items-center gap-2 mt-5">
          <span className="text-xs uppercase tracking-wide text-muted-foreground font-body mr-1">Teilen</span>
          <a href={`https://wa.me/?text=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp" title="WhatsApp" className={shareBtn}><MessageCircle size={16} /></a>
          <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`} target="_blank" rel="noreferrer" data-testid="share-x" title="X / Twitter" className={shareBtn}><Twitter size={16} /></a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" data-testid="share-facebook" title="Facebook" className={shareBtn}><Facebook size={16} /></a>
          <button onClick={copyLink} data-testid="share-copy" title="Link kopieren" className={shareBtn}><Link2 size={16} /></button>
        </div>

        {article.featured_image && (
          <figure className="mt-6">
            <img src={resolveImg(article.featured_image)} alt={article.title} className="w-full object-cover" />
            {(article.image_caption || article.image_credit) && (
              <figcaption className="mt-2 text-xs text-muted-foreground font-body flex justify-between gap-4 border-b border-edborder pb-3">
                <span>{article.image_caption}</span>
                <span className="shrink-0 uppercase tracking-wide">{article.image_credit}</span>
              </figcaption>
            )}
          </figure>
        )}

        <div
          data-testid="article-content-body"
          className="prose-europuls dropcap mt-8"
          dangerouslySetInnerHTML={{ __html: article.body }}
        />

        {article.tags?.length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="text-xs font-body uppercase tracking-wide bg-white border border-edborder text-prussian px-3 py-1">{t}</span>
            ))}
          </div>
        )}
      </article>

      {related.length > 0 && (
        <div className="max-w-[1240px] mx-auto px-4 pb-8">
          <div data-testid="article-related-grid" className="border-t-2 border-prussian pt-8">
            <h2 className="font-headline font-bold text-2xl text-prussian mb-6">Weiterführende Berichte</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}
